import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { Icon } from './Icon';
import type { NombreIcono } from './iconos';
import estilos from './Input.module.css';

interface BaseCampoProps {
  etiqueta?: string;
  /** Texto de apoyo bajo el campo, para explicar qué se espera. */
  ayuda?: string;
  error?: string | null;
  requerido?: boolean;
}

function Campo({
  id,
  etiqueta,
  ayuda,
  error,
  requerido,
  children,
}: BaseCampoProps & { id: string; children: ReactNode }) {
  return (
    <div className={[estilos.campo, error ? estilos.conError : ''].filter(Boolean).join(' ')}>
      {etiqueta && (
        <label className={estilos.etiqueta} htmlFor={id}>
          {etiqueta}
          {requerido && (
            <span className={estilos.requerido} aria-hidden>
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <span className={estilos.error} id={`${id}-error`} role="alert">
          <Icon nombre="errorContorno" tamano={13} />
          {error}
        </span>
      ) : (
        ayuda && (
          <span className={estilos.ayuda} id={`${id}-ayuda`}>
            {ayuda}
          </span>
        )
      )}
    </div>
  );
}

interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'children'>, BaseCampoProps {
  icono?: NombreIcono;
  /** Muestra un botón para alternar la visibilidad de la contraseña. */
  alternarVisibilidad?: boolean;
  /**
   * Impide que el navegador rellene el campo por su cuenta.
   * `autocomplete="off"` no basta: el gestor de contraseñas de Chrome lo
   * ignora cuando cree reconocer un campo de usuario. Un campo de solo
   * lectura no se rellena al cargar, y se desbloquea al primer toque.
   *
   * Los buscadores lo activan solos (ver más abajo); sólo hace falta indicarlo
   * a mano en campos de formulario que Chrome confunda con un usuario.
   */
  sinAutocompletar?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    etiqueta,
    ayuda,
    error,
    requerido,
    icono,
    alternarVisibilidad,
    sinAutocompletar,
    type = 'text',
    className,
    id,
    onFocus,
    readOnly,
    autoComplete,
    ...resto
  },
  ref,
) {
  const idGenerado = useId();
  const idCampo = id ?? idGenerado;
  const [visible, setVisible] = useState(false);

  /*
   * Un buscador nunca debe autorrellenarse. Chrome mete el correo guardado en
   * cuanto ve un campo de texto suelto, y como el buscador filtra al escribir,
   * la lista aparece vacía y parece que no hay datos.
   *
   * Se deduce del campo en lugar de pedirlo en cada pantalla: hay 23
   * buscadores y basta con que a uno se le olvide para que reaparezca el
   * problema. La lupa y `type="search"` son lo que los identifica.
   */
  const esBuscador = icono === 'buscar' || type === 'search';
  const evitarRelleno = sinAutocompletar ?? esBuscador;

  const [bloqueado, setBloqueado] = useState(evitarRelleno);

  const tipoFinal = alternarVisibilidad ? (visible ? 'text' : 'password') : type;

  return (
    <Campo id={idCampo} etiqueta={etiqueta} ayuda={ayuda} error={error} requerido={requerido}>
      <div className={estilos.envoltorio}>
        {icono && (
          <span className={estilos.iconoIzq}>
            <Icon nombre={icono} tamano={18} />
          </span>
        )}
        <input
          ref={ref}
          id={idCampo}
          type={tipoFinal}
          required={requerido}
          readOnly={bloqueado || readOnly}
          autoComplete={evitarRelleno ? 'off' : autoComplete}
          onFocus={(evento) => {
            if (bloqueado) setBloqueado(false);
            onFocus?.(evento);
          }}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${idCampo}-error` : ayuda ? `${idCampo}-ayuda` : undefined}
          className={[
            estilos.entrada,
            icono ? estilos.conIconoIzq : '',
            alternarVisibilidad ? estilos.conIconoDer : '',
            className,
          ]
            .filter(Boolean)
            .join(' ')}
          {...resto}
        />
        {alternarVisibilidad && (
          <button
            type="button"
            className={estilos.accion}
            onClick={() => setVisible((v) => !v)}
            aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            tabIndex={-1}
          >
            <Icon nombre={visible ? 'ocultar' : 'ver'} tamano={18} />
          </button>
        )}
      </div>
    </Campo>
  );
});

export interface OpcionSelect {
  valor: string | number;
  etiqueta: string;
  deshabilitada?: boolean;
}

interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'>, BaseCampoProps {
  opciones: readonly OpcionSelect[];
  /** Texto de la opción vacía inicial. Si se omite, no hay opción vacía. */
  placeholder?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { etiqueta, ayuda, error, requerido, opciones, placeholder, className, id, ...resto },
  ref,
) {
  const idGenerado = useId();
  const idCampo = id ?? idGenerado;

  return (
    <Campo id={idCampo} etiqueta={etiqueta} ayuda={ayuda} error={error} requerido={requerido}>
      <div className={estilos.envoltorio}>
        <select
          ref={ref}
          id={idCampo}
          required={requerido}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${idCampo}-error` : ayuda ? `${idCampo}-ayuda` : undefined}
          className={[estilos.entrada, estilos.select, className].filter(Boolean).join(' ')}
          {...resto}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {opciones.map((o) => (
            <option key={o.valor} value={o.valor} disabled={o.deshabilitada}>
              {o.etiqueta}
            </option>
          ))}
        </select>
      </div>
    </Campo>
  );
});
